import { http } from '@/lib/http';
import type { AllocationPlan, AllocationPlanEnvelope, BookAllResponse, BookAllRow } from './types';

// Bulk hotel-allocation client. Two calls:
//   POST .../allocation/auto      → build the plan (books nothing)
//   POST .../allocation/book-all  → book every plan item as a batch
// The BFF auto-acquires/releases each group's lock during book-all — the
// operator never locks anything explicitly.

function base(caseUrn: string): string {
  return `v1/cases/${encodeURIComponent(caseUrn)}/allocation`;
}

/**
 * The reviewed rows as "Book all" sends them: identity, rooms and stay — the amounts
 * the plan shows are the airline's PRICE and never travel back (commercial-contract
 * D16; the BFF would ignore them anyway — it books its own copy, booking prices the
 * offer). Pure.
 */
export function reviewedRows(plan: AllocationPlan): BookAllRow[] {
  return plan.plan.map((p) => ({
    subCaseUrn: p.subCaseUrn,
    pnr: p.pnr,
    ...(p.caseUrn ? { caseUrn: p.caseUrn } : {}),
    offerUrn: p.offerUrn,
    hotelUrn: p.hotelUrn,
    hotelName: p.hotelName,
    ...(p.hotelGeo ? { hotelGeo: p.hotelGeo } : {}),
    roomsNeeded: p.roomsNeeded,
    paxCount: p.paxCount,
    ...(p.checkInUtc ? { checkInUtc: p.checkInUtc } : {}),
    ...(p.checkOutUtc ? { checkOutUtc: p.checkOutUtc } : {}),
  }));
}

export interface AutoAllocateRequest {
  airportCode?: string;
}

/** Auto-allocate a policy-compliant hotel to every PNR group needing one. */
export async function autoAllocate(
  caseUrn: string,
  req: AutoAllocateRequest = {},
): Promise<AllocationPlan> {
  return http.post(`${base(caseUrn)}/auto`, { json: req }).json<AllocationPlan>();
}

/** Book the reviewed plan as a batch (partial failures reported per group). */
export async function bookAllPlan(
  caseUrn: string,
  plan: AllocationPlan,
  req: AutoAllocateRequest = {},
): Promise<BookAllResponse> {
  return http
    .post(`${base(caseUrn)}/book-all`, {
      json: { ...req, plan: reviewedRows(plan) },
      // The batch books many groups end-to-end — give it room past the default 10s.
      timeout: 60_000,
    })
    .json<BookAllResponse>();
}

// EVENT scope — the disruption-wide review-then-book-all. Same plan/result shapes
// as the case-scoped pair; the BFF fans the case planner + saga out across the
// disruption's child cases (each plan row carries its airportCode + caseUrn).
//   POST /v1/disruptions/:urn/allocation/plan      → merged review plan
//   POST /v1/disruptions/:urn/allocation/book-all  → book every reviewed row

function eventBase(disruptionUrn: string): string {
  return `v1/disruptions/${encodeURIComponent(disruptionUrn)}/allocation`;
}

/** Build the merged, reviewable allocation plan for the whole disruption. The BFF
 *  PERSISTS it (shared, TTL'd) + broadcasts it to every operator on the event. */
export async function planDisruptionAllocation(disruptionUrn: string): Promise<AllocationPlan> {
  return http
    .post(`${eventBase(disruptionUrn)}/plan`, { json: {}, timeout: 60_000 })
    .json<AllocationPlan>();
}

/** The SHARED stored plan for the disruption (whoever built it), so any operator's
 *  Hotels tab loads what another operator built. 204 (none built / expired) → null. */
export async function getStoredDisruptionPlan(
  disruptionUrn: string,
): Promise<AllocationPlanEnvelope | null> {
  const res = await http.get(`${eventBase(disruptionUrn)}/plan`);
  if (res.status === 204) return null;
  return (await res.json()) as AllocationPlanEnvelope;
}

/** Book every reviewed row across the event (fan-out, per-case saga). */
export async function bookAllDisruption(
  disruptionUrn: string,
  plan: AllocationPlan,
): Promise<BookAllResponse> {
  return http
    .post(`${eventBase(disruptionUrn)}/book-all`, {
      json: { plan: reviewedRows(plan) },
      timeout: 120_000,
    })
    .json<BookAllResponse>();
}
